import { SkinnedOwnerStageElement } from "./skinned-owner-stage.js";
import { escapeHtml } from "../util/escape-html.js";
import { setThemePreference } from "../shell/theme-init.js";
import econLogoUrl from "../skin/brand/econ-logo.png?url";
import nodeMarkUrl from "../skin/brand/node-mark.jpg?url";
const QUERY = "identity.status.v1";
const DEVICES_COMMAND = "client-device.list.v1";
export const PROFILE_STAGE_TAG = "node-profile-stage";
export const PROFILE_QUERY = QUERY;
export const PROFILE_DEVICES_COMMAND = DEVICES_COMMAND;
/**
 * Owner stage: profile — re-clothed to the original skin's full `.prof2-page` DOM skeleton
 * (epic #1507 fidelity, stage 3/5). Ported from `SKIN/stages/default/profile/view/view.js`,
 * verified real via explicit-file grep against the vendored `client/ui/src/skin/stages-css/
 * profile.css` + `client/ui/src/skin/css/legacy-numbered.css` (every class below — `.p2-memory`
 * through `.p2-econ`, the 6-column `.p2-set-grid`, the 10-column `.prof2-grid` spans — resolved
 * to a real vendored rule; nothing here relies on unvendored CSS). This is a DOM-template pass
 * only — no CSS edits, no new query wiring beyond what stage 1's original pass already had.
 *
 * The original mockup is a single hard-coded demo profile (device model, memory/backup/store
 * numbers, NFC balances, Wi-Fi SSID, model roster, etc. — none of it backed by a real kernel
 * query today). Per the data policy this ports the ORIGINAL DOM SHAPE, decorative SVGs kept
 * verbatim, but every fabricated number/name collapses to `"—"` rather than being reproduced —
 * see `docs/architecture/client-node-debt.md` item and issue #1539, which owns wiring memory/
 * node-service/NFC/privacy/backup/store/econ metrics to real queries once they exist. Two
 * exceptions, both real data kept exactly as before:
 *
 * - **Card 03 "Your devices."** binds the real `client-device.list.v1` command (already used
 *   by `NodeDeviceListElement`) — unchanged behavior, just renumbered/reclassed to sit in its
 *   original grid position among the other 8 cards instead of standing alone.
 * - **The hero `.did-card-mini-id`** reads a `did` field off the `identity.status.v1` row if
 *   one is ever present (`stringField(identity.did)`), else `"—"`. `identity.status.v1`
 *   (`IdentityStatusService`) only forwards `custodyMode`/`status` today — the real DID string
 *   lives at the lower `client/ports/src/identity.ts#DevicePublicIdentity.did` layer and isn't
 *   surfaced through `KernelRpcClient` yet (same gap this stage's earlier pass documented) — so
 *   this always renders `"—"` right now, but picks up a real value for free the moment #1539
 *   (or a future task) adds `did` to the query row, without another DOM change.
 * - `identity.status.v1` still drives this stage's `SkinnedOwnerStageElement` lifecycle
 *   (loading/ready/empty/error) even though its `custodyMode`/`status` fields are no longer
 *   rendered as hero text — the query itself is the "identity binding" being kept real, not a
 *   particular label derived from it.
 *
 * DOM notes:
 * - `.did-card-mini` (hero) and `.store-did-card` (card 07) are both present in the DOM, exactly
 *   as the original markup has them, but the ALREADY-VENDORED `.main-view[data-view="profile"]
 *   .did-card-mini{display:none !important}` / `...store-did-card{display:none !important}`
 *   rules hide them on this stage — verified against `legacy-numbered.css`, not something this
 *   port introduced. Matches upstream's own behavior for this view.
 * - `.prof2-ava` shows the literal placeholder initials `"TD"` ("This Device") and `.prof2-name`
 *   keeps the original's literal `"This device."` copy; a real per-user display name has no
 *   backing query (flagged #1539) rather than being invented.
 * - Card 07's `.se-sparkline` keeps the original's chart chrome but replaces its fabricated
 *   11-point earnings curve with a flat baseline — a shape implies a trend, which would be
 *   inventing data just as much as a number would.
 * - Card 08 "Settings." — 5 of 6 tiles are inert (`"—"` values, matching the original's plain,
 *   non-`<button>` `.p2-set-tile` markup, unwired to anything). The 6th, `#p2ThemeTile`
 *   (already a `<button>` in the original markup), is wired for real: click toggles the shell's
 *   dark theme via `shell/theme-init.ts`'s `setThemePreference`, persisted under the same
 *   `nodeos-theme` key the shell's own boot-time `initTheme` reads, applied to both the shadow
 *   host (`<node-app-shell>`, resolved via `getRootNode()`) and `document.documentElement` —
 *   so a toggle here survives reload and is visible immediately without a manual page refresh.
 */
export class ProfileStageElement extends SkinnedOwnerStageElement {
    query = QUERY;
    emptyMessage = "Device identity isn't available yet.";
    #devices = [];
    #devicesClient;
    #devicesGeneration = 0;
    configure(options) {
        super.configure(options);
        this.#devicesClient = options.client;
        if (this.isConnected)
            this.#loadDevices();
    }
    connectedCallback() {
        super.connectedCallback();
        this.#loadDevices();
    }
    disconnectedCallback() {
        super.disconnectedCallback();
        this.#devicesGeneration += 1;
    }
    renderView(root, rows) {
        const identity = rows[0] ?? {};
        const did = stringField(identity.did) ?? "—";
        root.innerHTML = `
      <div class="prof2-page" data-profile-page>
        <div class="prof2-hero">
          <div class="prof2-ava-wrap">
            <div class="prof2-ava">TD</div>
            <div class="prof2-hero-text">
              <div class="prof2-name">This <em>device.</em></div>
              <div class="prof2-handle">—</div>
            </div>
          </div>
          <div class="did-card-mini">
            <div class="did-card-mini-chip"></div>
            <div class="did-card-mini-tag">DID:human</div>
            <div class="did-card-mini-id">${escapeHtml(did)}</div>
          </div>
          <button class="prof2-edit-btn" type="button" disabled title="Coming soon">Edit profile</button>
        </div>
        <div class="prof2-grid">
          ${renderMemoryCard()}
          ${renderNodeCard()}
          <div class="prof2-card p2-devices">
            <div class="p2-card-hd">
              <div class="p2-eyebrow">03</div>
              <h3>Your <em>devices.</em></h3>
            </div>
            <div class="p2-dev-row" data-profile-devices>${renderDevices(this.#devices)}</div>
          </div>
          ${renderNfcCard()}
          ${renderPrivacyCard()}
          ${renderBackupCard()}
          ${renderStoreCard()}
          ${renderSettingsCard()}
          ${renderEconCard()}
        </div>
      </div>
    `;
        this.#wireThemeTile(root);
    }
    #loadDevices() {
        const client = this.#devicesClient;
        if (client === undefined)
            return;
        const generation = ++this.#devicesGeneration;
        void client.command(DEVICES_COMMAND, { includeRevoked: true })
            .then((devices) => {
            if (generation !== this.#devicesGeneration)
                return;
            this.#devices = devices;
            const slot = this.querySelector("[data-profile-devices]");
            if (slot !== null)
                slot.innerHTML = renderDevices(this.#devices);
        })
            .catch(() => {
            // Offline/error: leave whatever device list was already rendered (possibly empty).
        });
    }
    #wireThemeTile(root) {
        const tile = root.querySelector("#p2ThemeTile");
        if (tile === null)
            return;
        tile.addEventListener("click", () => {
            const next = document.documentElement.classList.contains("dark") ? "light" : "dark";
            setThemePreference(next, this.#themeHost());
            this.#applyThemeTileState();
        });
        this.#applyThemeTileState();
    }
    #applyThemeTileState() {
        const tile = this.querySelector("#p2ThemeTile");
        if (tile === null)
            return;
        const dark = document.documentElement.classList.contains("dark");
        tile.setAttribute("aria-pressed", dark ? "true" : "false");
        const state = tile.querySelector("[data-theme-state]");
        if (state !== null)
            state.textContent = dark ? "On" : "Off";
    }
    /** The shadow host (`<node-app-shell>`) `setThemePreference` applies theme classes to,
     * mirroring `shell/theme-init.ts`'s own contract — resolved via `getRootNode()` the same
     * way `SkinnedOwnerStageElement#railContainer` locates the shell's `.rail`. Falls back to
     * `document.documentElement` when this element isn't mounted inside a shadow root (a plain
     * test harness, or no shell wraps this stage) — `applyTheme` always toggles
     * `document.documentElement` too, so this is harmless, not just a stand-in. */
    #themeHost() {
        const root = this.getRootNode();
        return root instanceof ShadowRoot ? root.host : document.documentElement;
    }
}
export function registerProfileStageElement() {
    if (!customElements.get(PROFILE_STAGE_TAG)) {
        customElements.define(PROFILE_STAGE_TAG, ProfileStageElement);
    }
}
// ---------------------------------------------------------------------------------------------
// 01 · Your memory.
// ---------------------------------------------------------------------------------------------
function renderMemoryCard() {
    return `
          <div class="prof2-card p2-memory">
            <div class="p2-card-hd">
              <div class="p2-eyebrow">01</div>
              <h3>Your <em>memory.</em></h3>
            </div>
            <div class="p2-memory-viz">
              <div class="p2-mem-icon">
                <svg viewBox="0 0 58 48" shape-rendering="crispEdges" preserveAspectRatio="none">
                  <rect x="0" y="0" width="58" height="4" fill="#0A0A0A"/>
                  <rect x="0" y="5" width="27" height="22" fill="#2B7258"/>
                  <rect x="28" y="5" width="17" height="14" fill="#DF6662"/>
                  <rect x="46" y="5" width="12" height="14" fill="#C0CAE8"/>
                  <rect x="28" y="20" width="30" height="7" fill="#B85551"/>
                  <rect x="0" y="28" width="12" height="20" fill="#C0CAE8"/>
                  <rect x="13" y="28" width="17" height="10" fill="#315D6B"/>
                  <rect x="13" y="39" width="17" height="9" fill="#DF6662"/>
                  <rect x="31" y="28" width="12" height="20" fill="#5C6F4A"/>
                  <rect x="44" y="28" width="14" height="10" fill="#DF6662"/>
                  <rect x="44" y="39" width="14" height="9" fill="#B85551"/>
                </svg>
              </div>
              <div class="p2-mem-stats">
                <div class="p2-mem-big">—<span class="u">GB</span><span class="of">of —GB used</span></div>
                <div class="p2-mem-bar"><div class="p2-mem-bar-fill" style="width: 0%;"></div></div>
              </div>
            </div>
            <div class="p2-mem-sched p2-mem-sched-table">
              <div class="p2-mem-sched-row sched-nightly">
                <div class="p2-mem-sched-ico">
                  <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" stroke-width="1.4" stroke-linejoin="round">
                    <rect x="2" y="4" width="10" height="10"/>
                    <rect x="10" y="2" width="4" height="4" fill="currentColor"/>
                  </svg>
                </div>
                <div class="p2-mem-sched-label">Nightly compaction</div>
                <div class="p2-mem-sched-val">—</div>
              </div>
              <div class="p2-mem-sched-row sched-weekly">
                <div class="p2-mem-sched-ico">
                  <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" stroke-width="1.4" stroke-linecap="round" stroke-linejoin="round">
                    <path d="M8 3 V13"/>
                    <path d="M8 6 L4 9"/>
                    <path d="M8 6 L12 9"/>
                    <circle cx="8" cy="2.5" r="1.3" fill="currentColor" stroke="none"/>
                    <circle cx="4" cy="9.5" r="1.3" fill="currentColor" stroke="none"/>
                    <circle cx="12" cy="9.5" r="1.3" fill="currentColor" stroke="none"/>
                    <circle cx="8" cy="13" r="1.3" fill="currentColor" stroke="none"/>
                  </svg>
                </div>
                <div class="p2-mem-sched-label">Weekly organization</div>
                <div class="p2-mem-sched-val">—</div>
              </div>
            </div>
            <button class="p2-primary-btn" id="p2MemOpen" type="button" disabled title="Coming soon">
              <svg viewBox="0 0 16 14" fill="none" stroke="currentColor" stroke-width="1.3">
                <rect x="1" y="3" width="14" height="8" rx="1" fill="currentColor" stroke="none"/>
                <rect x="3.5" y="5" width="9" height="1" fill="var(--bg-0)" stroke="none"/>
                <rect x="3.5" y="7" width="9" height="1" fill="var(--bg-0)" stroke="none"/>
                <rect x="3.5" y="9" width="6" height="1" fill="var(--bg-0)" stroke="none"/>
              </svg>
              Open Memory
            </button>
          </div>`;
}
// ---------------------------------------------------------------------------------------------
// 02 · Your node.
// ---------------------------------------------------------------------------------------------
function renderNodeCard() {
    return `
          <div class="prof2-card p2-node">
            <div class="p2-card-hd">
              <div class="p2-eyebrow">02</div>
              <h3>Your <em>node.</em></h3>
            </div>
            <div class="p2-node-viz">
              <svg viewBox="0 0 140 160" class="p2-node-cyl" preserveAspectRatio="xMidYMid meet">
                <defs>
                  <linearGradient id="p2cyl-body" x1="0" y1="0" x2="1" y2="0">
                    <stop offset="0%" stop-color="#16141210"/>
                    <stop offset="8%" stop-color="#1F1C19"/>
                    <stop offset="50%" stop-color="#2F2B26"/>
                    <stop offset="92%" stop-color="#1A1814"/>
                    <stop offset="100%" stop-color="#0C0A08"/>
                  </linearGradient>
                  <linearGradient id="p2cyl-top" x1="0" y1="0" x2="1" y2="0">
                    <stop offset="0%" stop-color="#2A2620"/>
                    <stop offset="40%" stop-color="#3E3830"/>
                    <stop offset="75%" stop-color="#322D27"/>
                    <stop offset="100%" stop-color="#1B1813"/>
                  </linearGradient>
                  <linearGradient id="p2cyl-bot" x1="0" y1="0" x2="1" y2="0">
                    <stop offset="0%" stop-color="#08070600"/>
                    <stop offset="50%" stop-color="#0A0907"/>
                    <stop offset="100%" stop-color="#030202"/>
                  </linearGradient>
                  <radialGradient id="p2cyl-topHighlight" cx="0.5" cy="0.35" r="0.5">
                    <stop offset="0%" stop-color="rgba(255,255,255,0.12)"/>
                    <stop offset="100%" stop-color="rgba(255,255,255,0)"/>
                  </radialGradient>
                </defs>
                <ellipse cx="70" cy="148" rx="48" ry="5" fill="rgba(10,10,10,0.18)"/>
                <ellipse cx="70" cy="138" rx="44" ry="8" fill="url(#p2cyl-bot)"/>
                <rect x="26" y="22" width="88" height="116" fill="url(#p2cyl-body)"/>
                <rect x="30" y="22" width="0.8" height="116" fill="rgba(255,255,255,0.04)"/>
                <rect x="109" y="22" width="0.8" height="116" fill="rgba(0,0,0,0.35)"/>
                <g stroke="#0A0908" stroke-width="0.5" opacity="0.75">
                  <line x1="36" y1="58" x2="104" y2="58"/>
                  <line x1="36" y1="62" x2="104" y2="62"/>
                  <line x1="36" y1="66" x2="104" y2="66"/>
                  <line x1="36" y1="70" x2="104" y2="70"/>
                  <line x1="36" y1="74" x2="104" y2="74"/>
                  <line x1="36" y1="78" x2="104" y2="78"/>
                  <line x1="36" y1="82" x2="104" y2="82"/>
                </g>
                <text x="70" y="106" text-anchor="middle" font-family="JetBrains Mono, monospace" font-size="8" letter-spacing="4" fill="rgba(255,255,255,0.22)" font-weight="600">NODE</text>
                <circle cx="70" cy="122" r="1.1" fill="#36AA75"/>
                <circle cx="70" cy="122" r="0.5" fill="#36AA75"/>
                <ellipse cx="70" cy="22" rx="44" ry="9" fill="url(#p2cyl-top)"/>
                <ellipse cx="70" cy="21" rx="44" ry="9" fill="url(#p2cyl-topHighlight)"/>
                <ellipse cx="70" cy="22" rx="40" ry="7.5" fill="none" stroke="rgba(0,0,0,0.35)" stroke-width="0.6"/>
                <ellipse cx="70" cy="20" rx="34" ry="5.5" fill="#0A0907"/>
                <ellipse cx="70" cy="19" rx="34" ry="5.5" fill="url(#p2cyl-topHighlight)" opacity="0.5"/>
                <rect x="61" y="132" width="18" height="2.2" rx="0.4" fill="#050403"/>
              </svg>
              <div class="p2-node-info">
                <div class="p2-node-service-row srv-bitcoin">
                  <div class="p2-srv-led"></div>
                  <div class="p2-srv-label">Bitcoin node</div>
                  <div class="p2-srv-status">—</div>
                </div>
                <div class="p2-node-service-row srv-lightning">
                  <div class="p2-srv-led"></div>
                  <div class="p2-srv-label">Lightning node</div>
                  <div class="p2-srv-status">—</div>
                </div>
                <div class="p2-node-wifi">
                  <svg viewBox="0 0 12 12" fill="none" stroke="currentColor" stroke-width="1.2" stroke-linecap="round" stroke-linejoin="round">
                    <path d="M1.5 5 Q6 2 10.5 5 M3 7 Q6 5 9 7 M4.5 9 Q6 8 7.5 9"/>
                    <circle cx="6" cy="10.5" r="0.6" fill="currentColor"/>
                  </svg>
                  <span class="p2-wifi-label">wifi</span>
                  <span class="p2-wifi-name">—</span>
                </div>
              </div>
            </div>
            <div class="p2-node-econ-sub">
              <span class="p2-econ-sub-label">running on</span>
              <img class="p2-node-econ-logo" id="econLogoSlot" alt="Economy V1" src="${econLogoUrl}">
            </div>
          </div>`;
}
// ---------------------------------------------------------------------------------------------
// 03 · Your devices. (real client-device.list.v1 rows)
// ---------------------------------------------------------------------------------------------
function renderDevices(devices) {
    const rows = devices.length > 0
        ? devices.map((device) => renderDeviceRow(device)).join("")
        : `<p class="ui-eyebrow">No paired devices found</p>`;
    return `${rows}${renderPairNewTile()}`;
}
function renderDeviceRow(device) {
    const name = stringField(device.deviceName) ?? "Unnamed device";
    const type = stringField(device.deviceType) ?? "device";
    const status = stringField(device.status) ?? "unknown";
    const icon = deviceIconInfo(type);
    return `
              <div class="p2-dev">
                <div class="${icon.wrapperClass}">${icon.svg}</div>
                <div class="p2-dev-name" title="${escapeHtml(name)}">${escapeHtml(name)}</div>
                <div class="p2-dev-last">${escapeHtml(status)}</div>
              </div>`;
}
function renderPairNewTile() {
    return `
              <button type="button" class="p2-dev p2-dev-add" disabled title="Coming soon">
                <div class="p2-dev-icon p2-dev-plus">
                  <svg viewBox="0 0 40 40" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round">
                    <rect x="3" y="3" width="34" height="34" rx="4" stroke-dasharray="3 3"/>
                    <path d="M20 12 V28 M12 20 H28"/>
                  </svg>
                </div>
                <div class="p2-dev-name">Pair new</div>
                <div class="p2-dev-last">QR · Bluetooth</div>
              </button>`;
}
function deviceIconInfo(type) {
    const normalized = type.toLowerCase();
    if (normalized.includes("mobile") || normalized.includes("phone")) {
        return {
            svg: '<svg viewBox="0 0 28 52" fill="none" stroke="currentColor" stroke-width="1.4" stroke-linecap="round" stroke-linejoin="round"><rect x="1.5" y="1" width="25" height="50" rx="4"/><circle cx="14" cy="46" r="1.2" fill="currentColor"/><path d="M11 4.5 H17"/></svg>',
            wrapperClass: "p2-dev-icon p2-dev-phone",
        };
    }
    if (normalized.includes("tablet") || normalized.includes("pad")) {
        return {
            svg: '<svg viewBox="0 0 60 80" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"><rect x="1.5" y="1" width="57" height="78" rx="5"/><circle cx="30" cy="74" r="1.5" fill="currentColor"/></svg>',
            wrapperClass: "p2-dev-icon p2-dev-pad",
        };
    }
    return {
        svg: '<svg viewBox="0 0 130 80" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"><rect x="8" y="6" width="114" height="60" rx="3"/><path d="M1.5 66 H128.5 L124 75 H6 Z"/><path d="M55 70 H75"/></svg>',
        wrapperClass: "p2-dev-icon p2-dev-laptop",
    };
}
// ---------------------------------------------------------------------------------------------
// 04 · Your NFC cards.
// ---------------------------------------------------------------------------------------------
function renderNfcCard() {
    return `
          <div class="prof2-card p2-nfc">
            <div class="p2-card-hd">
              <div class="p2-eyebrow">04</div>
              <h3>Your <em>NFC cards.</em></h3>
            </div>
            <div class="p2-nfc-stack">
              ${renderNfcShell("nfc-spend-a", false)}
              ${renderNfcShell("nfc-spend-b", false)}
              ${renderNfcShell("nfc-backup nfc-cc-black", true)}
            </div>
          </div>`;
}
function renderNfcShell(faceClass, isBackup) {
    const body = isBackup
        ? `<div class="nfc-store-safely-text">store safely</div>`
        : `<div class="nfc-balance">—<span class="nfc-u">s</span></div>`;
    return `
              <div class="p2-nfc-cc ${faceClass}">
                <div class="nfc-cc-hd">
                  <div class="nfc-label">Card slot</div>
                  <img class="nfc-logo" src="${nodeMarkUrl}" alt="Node">
                </div>
                <div class="nfc-cc-body">
                  <div class="nfc-chip"></div>
                  ${body}
                </div>
                <div class="nfc-cc-foot">•••• ————</div>
              </div>`;
}
// ---------------------------------------------------------------------------------------------
// 05 · Your privacy.
// ---------------------------------------------------------------------------------------------
function renderPrivacyCard() {
    return `
          <div class="prof2-card p2-privacy-v2">
            <div class="p2-card-hd">
              <div class="p2-eyebrow">05</div>
              <h3>Your <em>privacy.</em></h3>
            </div>
            <div class="p2-priv-group">
              <div class="p2-priv-row">
                <div class="p2-priv-text">
                  <div class="p2-priv-name">Private LLM calls</div>
                  <div class="p2-priv-sub">Route sensitive prompts through on-node models</div>
                </div>
                <div class="p2-priv-ctl">
                  <div class="p2-seg">
                    <button class="p2-seg-btn" type="button" disabled>low</button>
                    <button class="p2-seg-btn" type="button" disabled>med</button>
                    <button class="p2-seg-btn" type="button" disabled>high</button>
                  </div>
                </div>
              </div>
              <div class="p2-priv-row">
                <div class="p2-priv-text">
                  <div class="p2-priv-name">Auto redaction</div>
                  <div class="p2-priv-sub">Strip PII &amp; secrets before outbound API calls</div>
                </div>
                <div class="p2-priv-ctl">
                  <button class="p2-sw" type="button" disabled aria-pressed="false" data-sw></button>
                </div>
              </div>
              <div class="p2-priv-row">
                <div class="p2-priv-text">
                  <div class="p2-priv-name">Zero data retention</div>
                  <div class="p2-priv-sub">Only models with zero-data-retention contracts may receive your data</div>
                </div>
                <div class="p2-priv-ctl">
                  <button class="p2-sw" type="button" disabled aria-pressed="false" data-sw></button>
                </div>
              </div>
              <div class="p2-priv-models">
                <div class="p2-priv-models-label">Zero-data-retention eligible models</div>
                <div class="p2-priv-models-list">
                  <span class="p2-model-pill">—</span>
                </div>
              </div>
            </div>
          </div>`;
}
// ---------------------------------------------------------------------------------------------
// 06 · Distributed backup.
// ---------------------------------------------------------------------------------------------
function renderBackupCard() {
    return `
          <div class="prof2-card p2-backup">
            <div class="p2-card-hd">
              <div class="p2-eyebrow">06</div>
              <h3>Distributed <em>backup.</em></h3>
            </div>
            <div class="p2-bkp-viz">
              <svg viewBox="0 0 220 120" class="p2-bkp-mesh">
                <g stroke="var(--hr-strong)" stroke-width="1" fill="none" opacity="0.6">
                  <line x1="110" y1="60" x2="35" y2="22"/>
                  <line x1="110" y1="60" x2="185" y2="20"/>
                  <line x1="110" y1="60" x2="25" y2="60"/>
                  <line x1="110" y1="60" x2="195" y2="60"/>
                  <line x1="110" y1="60" x2="40" y2="100"/>
                  <line x1="110" y1="60" x2="180" y2="100"/>
                  <line x1="110" y1="60" x2="78" y2="16"/>
                  <line x1="110" y1="60" x2="142" y2="16"/>
                  <line x1="110" y1="60" x2="78" y2="108"/>
                  <line x1="110" y1="60" x2="142" y2="108"/>
                </g>
                <g fill="var(--green)">
                  <circle cx="35" cy="22" r="4"/>
                  <circle cx="185" cy="20" r="4"/>
                  <circle cx="25" cy="60" r="4"/>
                  <circle cx="195" cy="60" r="4"/>
                  <circle cx="40" cy="100" r="4"/>
                  <circle cx="180" cy="100" r="4"/>
                  <circle cx="78" cy="16" r="3"/>
                  <circle cx="142" cy="16" r="3"/>
                  <circle cx="78" cy="108" r="3"/>
                  <circle cx="142" cy="108" r="3"/>
                </g>
                <circle cx="110" cy="60" r="9" fill="var(--ink-hi)" stroke="var(--bg-0)" stroke-width="2"/>
                <circle cx="110" cy="60" r="14" fill="none" stroke="var(--gold)" stroke-width="1.2" opacity="0.7">
                  <animate attributeName="r" from="9" to="20" dur="2.4s" repeatCount="indefinite"/>
                  <animate attributeName="opacity" from="0.7" to="0" dur="2.4s" repeatCount="indefinite"/>
                </circle>
              </svg>
              <div class="p2-bkp-stats">
                <div class="p2-bkp-stat-big">— <span class="u">nodes</span></div>
                <div class="p2-bkp-stat-sub">—</div>
              </div>
            </div>
            <div class="p2-bkp-redundancy">
              <div class="p2-bkp-red-hd">
                <span class="p2-bkp-red-label">Backup redundancy</span>
                <span class="p2-bkp-red-val">—</span>
              </div>
              <div class="p2-seg p2-seg-wide">
                <button class="p2-seg-btn" type="button" disabled>1×</button>
                <button class="p2-seg-btn" type="button" disabled>2×</button>
                <button class="p2-seg-btn" type="button" disabled>3×</button>
                <button class="p2-seg-btn" type="button" disabled>4×</button>
                <button class="p2-seg-btn" type="button" disabled>5×</button>
              </div>
              <div class="p2-bkp-cost">
                <span class="p2-bkp-cost-label">avg cost</span>
                <span class="p2-bkp-cost-val">—</span>
              </div>
            </div>
            <div class="p2-bkp-off-row">
              <div class="p2-priv-text">
                <div class="p2-priv-name">Distributed backup</div>
                <div class="p2-priv-sub">Redundant encrypted shards stored across Economy V1</div>
              </div>
              <button class="p2-sw" type="button" disabled aria-pressed="false" data-sw></button>
            </div>
          </div>`;
}
// ---------------------------------------------------------------------------------------------
// 07 · My store.
// ---------------------------------------------------------------------------------------------
function renderStoreCard() {
    return `
          <div class="prof2-card p2-store">
            <div class="p2-card-hd">
              <div class="p2-eyebrow">07</div>
              <h3>My <em>store.</em></h3>
            </div>
            <div class="store-did-card">
              <div class="store-did-body">
                <div class="store-did-tag">DID:store</div>
                <div class="store-did-id">—</div>
              </div>
              <div class="store-did-chip"></div>
            </div>
            <div class="store-earnings">
              <div class="se-main">
                <div class="se-label">Current earnings <span class="se-period">· this month</span></div>
                <div class="se-amt">—<span class="se-u">sats</span></div>
                <div class="se-usd">≈ — USD</div>
              </div>
              <div class="se-sparkline">
                <svg viewBox="0 0 120 40" preserveAspectRatio="none">
                  <line x1="0" y1="20" x2="120" y2="20" stroke="var(--hr-strong)" stroke-width="1.8" stroke-linecap="round"/>
                </svg>
              </div>
            </div>
            <div class="store-stats">
              <div class="store-stat"><div class="ss-v">—</div><div class="ss-l">Sales</div></div>
              <div class="store-stat"><div class="ss-v">—</div><div class="ss-l">Products</div></div>
              <div class="store-stat"><div class="ss-v">—</div><div class="ss-l">Rating</div></div>
            </div>
            <button class="p2-text-btn" type="button" disabled title="Coming soon">Manage store ›</button>
          </div>`;
}
// ---------------------------------------------------------------------------------------------
// 08 · Settings.
// ---------------------------------------------------------------------------------------------
function renderSettingsCard() {
    return `
          <div class="prof2-card p2-settings">
            <div class="p2-card-hd">
              <div class="p2-eyebrow">08</div>
              <h3><em>Settings.</em></h3>
            </div>
            <div class="p2-set-grid">
              <div class="p2-set-tile">
                <div class="p2-set-ico tone-blue">
                  <svg viewBox="0 0 18 18" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round">
                    <path d="M2 7 Q9 3 16 7 M4 10 Q9 7.5 14 10 M6 13 Q9 11.5 12 13"/>
                    <circle cx="9" cy="15.5" r="0.9" fill="currentColor"/>
                  </svg>
                </div>
                <div class="p2-set-name">Wi-Fi</div>
                <div class="p2-set-val">—</div>
              </div>
              <button type="button" class="p2-set-tile p2-theme-tile" id="p2ThemeTile" aria-pressed="false" title="Toggle dark theme">
                <div class="p2-set-ico tone-orange">
                  <svg class="p2-theme-sun" viewBox="0 0 18 18" fill="none" stroke="currentColor" stroke-width="1.4" stroke-linecap="round" stroke-linejoin="round">
                    <circle cx="9" cy="9" r="3"/>
                    <path d="M9 2 V4 M9 14 V16 M2 9 H4 M14 9 H16 M4 4 L5.5 5.5 M12.5 12.5 L14 14 M14 4 L12.5 5.5 M5.5 12.5 L4 14"/>
                  </svg>
                  <svg class="p2-theme-moon" viewBox="0 0 18 18" fill="none" stroke="currentColor" stroke-width="1.4" stroke-linecap="round" stroke-linejoin="round">
                    <path d="M15 10.5 A6.2 6.2 0 1 1 7.5 3 A4.9 4.9 0 0 0 15 10.5 Z"/>
                  </svg>
                </div>
                <div class="p2-set-name">Dark theme</div>
                <div class="p2-set-val"><span class="p2-theme-state" data-theme-state>Off</span></div>
              </button>
              <div class="p2-set-tile">
                <div class="p2-set-ico tone-violet">
                  <svg viewBox="0 0 18 18" fill="none" stroke="currentColor" stroke-width="1.4" stroke-linecap="round" stroke-linejoin="round">
                    <path d="M3 9 Q3 5 9 5 Q15 5 15 9 V11 H3 Z"/>
                    <path d="M9 5 V3 M7 3 L9 1.5 L11 3"/>
                  </svg>
                </div>
                <div class="p2-set-name">Notifications</div>
                <div class="p2-set-val">—</div>
              </div>
              <div class="p2-set-tile">
                <div class="p2-set-ico tone-yellow">
                  <svg viewBox="0 0 18 18" fill="none" stroke="currentColor" stroke-width="1.4" stroke-linecap="round" stroke-linejoin="round">
                    <circle cx="9" cy="9" r="6.5"/>
                    <path d="M2.5 9 H15.5 M9 2.5 Q12 5 12 9 Q12 13 9 15.5 Q6 13 6 9 Q6 5 9 2.5"/>
                  </svg>
                </div>
                <div class="p2-set-name">Language</div>
                <div class="p2-set-val">—</div>
              </div>
              <div class="p2-set-tile">
                <div class="p2-set-ico tone-green">
                  <svg viewBox="0 0 18 18" fill="none" stroke="currentColor" stroke-width="1.4" stroke-linecap="round" stroke-linejoin="round">
                    <circle cx="9" cy="9" r="6.5"/>
                    <path d="M9 5.5 V9 L11.5 11"/>
                  </svg>
                </div>
                <div class="p2-set-name">Automations</div>
                <div class="p2-set-val">—</div>
              </div>
              <div class="p2-set-tile">
                <div class="p2-set-ico tone-red">
                  <svg viewBox="0 0 18 18" fill="none" stroke="currentColor" stroke-width="1.4" stroke-linecap="round" stroke-linejoin="round">
                    <path d="M11 4 H4 V14 H11 M11 9 H16 M14 6.5 L16 9 L14 11.5"/>
                  </svg>
                </div>
                <div class="p2-set-name">Sign out</div>
                <div class="p2-set-val">—</div>
              </div>
            </div>
          </div>`;
}
// ---------------------------------------------------------------------------------------------
// 09 · You're on Economy V1.
// ---------------------------------------------------------------------------------------------
function renderEconCard() {
    return `
          <div class="prof2-card p2-econ">
            <div class="p2-card-hd">
              <div class="p2-eyebrow">09 · Network</div>
              <h3>You're on <em>Economy V1.</em></h3>
            </div>
            <div class="p2-econ-viz">
              <img class="p2-econ-logo" src="${econLogoUrl}" alt="Economy V1">
              <div class="p2-econ-mesh">
                <svg viewBox="0 0 300 160" fill="none" stroke="currentColor" stroke-width="1">
                  <g class="p2-econ-lines">
                    <path d="M 40 80 L 120 40" stroke="currentColor" stroke-width="1" opacity="0.3"/>
                    <path d="M 40 80 L 120 120" stroke="currentColor" stroke-width="1" opacity="0.3"/>
                    <path d="M 120 40 L 200 80" stroke="currentColor" stroke-width="1" opacity="0.3"/>
                    <path d="M 120 120 L 200 80" stroke="currentColor" stroke-width="1" opacity="0.3"/>
                    <path d="M 200 80 L 280 40" stroke="currentColor" stroke-width="1" opacity="0.3"/>
                    <path d="M 200 80 L 280 120" stroke="currentColor" stroke-width="1" opacity="0.3"/>
                    <path d="M 120 40 L 120 120" stroke="currentColor" stroke-width="1" opacity="0.2"/>
                    <path d="M 40 80 L 200 80" stroke="currentColor" stroke-width="1" opacity="0.2"/>
                  </g>
                  <circle cx="40" cy="80" r="6" fill="var(--green-dp)"/>
                  <circle cx="120" cy="40" r="4" fill="currentColor" opacity="0.5"/>
                  <circle cx="120" cy="120" r="4" fill="currentColor" opacity="0.5"/>
                  <circle cx="200" cy="80" r="5" fill="currentColor" opacity="0.7"/>
                  <circle cx="280" cy="40" r="4" fill="currentColor" opacity="0.5"/>
                  <circle cx="280" cy="120" r="4" fill="currentColor" opacity="0.5"/>
                  <circle cx="40" cy="80" r="10" fill="none" stroke="var(--green)" stroke-width="1.5" opacity="0.5">
                    <animate attributeName="r" from="6" to="16" dur="2.2s" repeatCount="indefinite"/>
                    <animate attributeName="opacity" from="0.6" to="0" dur="2.2s" repeatCount="indefinite"/>
                  </circle>
                </svg>
              </div>
              <div class="p2-econ-stats">
                <div class="p2-econ-stat"><div class="v">—</div><div class="l">Peers</div></div>
                <div class="p2-econ-stat"><div class="v">—</div><div class="l">Latency</div></div>
                <div class="p2-econ-stat"><div class="v">—</div><div class="l">Uptime</div></div>
              </div>
            </div>
            <button class="p2-text-btn" type="button" disabled title="Coming soon">Network dashboard ›</button>
          </div>`;
}
function stringField(value) {
    return typeof value === "string" && value.length > 0 ? value : undefined;
}
//# sourceMappingURL=profile-stage.js.map